import { Link } from "react-router-dom";
import { RotateCw } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Panel } from "@/components/crm/Badges";
import { brand } from "@/lib/brand";

const STATUS = {
  procesado: "bg-emerald-50 text-emerald-700", error: "bg-rose-50 text-rose-700", fallido: "bg-rose-100 text-rose-800",
  recibido: "bg-sky-50 text-sky-700", pendiente: "bg-amber-50 text-amber-700", ignorado: "bg-slate-100 text-slate-600",
  info: "bg-sky-50 text-sky-700", warning: "bg-amber-50 text-amber-700",
};
const Pill = ({ s }) => <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS[s] || "bg-slate-100"}`}>{s}</span>;

export const Grid = ({ title, head, children, empty, testid }) => (
  <Panel title={title}>
    <div className="overflow-x-auto">
      <Table data-testid={testid}>
        <TableHeader><TableRow>{head.map((h) => <TableHead key={h} className="whitespace-nowrap text-xs uppercase">{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{children}</TableBody>
      </Table>
      {empty && <p className="py-8 text-center text-sm text-slate-400">Sin registros</p>}
    </div>
  </Panel>
);

export const GhlLogsTable = ({ logs, testid }) => (
  <Grid head={["Fecha", "Tipo", "Nivel", "Detalle"]} empty={!logs.length} testid={testid}>
    {logs.map((l) => (
      <TableRow key={l.id}>
        <TableCell className="whitespace-nowrap text-slate-500">{fmtDateTime(l.created_at)}</TableCell>
        <TableCell className="whitespace-nowrap font-medium">{l.kind}</TableCell>
        <TableCell><Pill s={l.level} /></TableCell>
        <TableCell className="min-w-[240px] text-xs">{brand(l.message)}{l.meta?.ip && <span className="text-slate-400"> · IP {l.meta.ip}</span>}</TableCell>
      </TableRow>
    ))}
  </Grid>
);

export const GhlEventsTable = ({ events }) => (
  <Grid head={["Fecha", "Tipo", "Canal", "Estado", "Detalle", "Intentos", "Duplicados", "Lead"]} empty={!events.length} testid="ghl-events-table">
    {events.map((e) => (
      <TableRow key={e.id} data-testid={`ghl-event-row-${e.id}`}>
        <TableCell className="whitespace-nowrap text-slate-500">{fmtDateTime(e.received_at)}</TableCell>
        <TableCell className="font-medium">{e.type}</TableCell>
        <TableCell className="whitespace-nowrap text-slate-600">{e.channel || "—"}{e.channel === "Otros" && e.message_type ? <span className="block text-[11px] text-slate-400">{e.message_type}</span> : null}</TableCell>
        <TableCell><Pill s={e.status} /></TableCell>
        <TableCell className={`max-w-[260px] text-xs ${["error", "fallido"].includes(e.status) ? "text-rose-600" : "text-slate-500"}`}>{brand(e.error) || "—"}</TableCell>
        <TableCell>{e.attempts || 0}</TableCell>
        <TableCell>{e.duplicate_count || 0}</TableCell>
        <TableCell>{e.lead_id ? <Link to={`/crm/leads/${e.lead_id}`} className="whitespace-nowrap text-[#1E6FD9]">{e.lead_name || "Ver"}</Link> : "—"}</TableCell>
      </TableRow>
    ))}
  </Grid>
);

const RetryBtn = ({ onClick, testid }) => (
  <Button variant="outline" size="sm" onClick={onClick} className="h-8 rounded-lg" data-testid={testid}><RotateCw className="mr-1 h-3.5 w-3.5" /> Reintentar</Button>
);

export const GhlFailedTables = ({ failed, onRetry }) => (
  <div className="space-y-6">
    <Grid title="Contactos pendientes de sincronización" head={["Fecha", "Lead", "Intentos", "Motivo", ""]} empty={!failed.queue.length} testid="ghl-failed-queue">
      {failed.queue.map((q) => (
        <TableRow key={q.id}>
          <TableCell className="whitespace-nowrap text-slate-500">{fmtDateTime(q.created_at)}</TableCell>
          <TableCell><Link to={`/crm/leads/${q.lead_id}`} className="text-[#1E6FD9]">{q.lead_name || "Lead"}</Link></TableCell>
          <TableCell>{q.attempts}</TableCell>
          <TableCell className="text-xs text-rose-600">{brand(q.last_error)}</TableCell>
          <TableCell><RetryBtn onClick={() => onRetry("queue", q.id)} testid={`retry-queue-${q.id}`} /></TableCell>
        </TableRow>
      ))}
    </Grid>
    <Grid title="Eventos con error" head={["Fecha", "Tipo", "Motivo", ""]} empty={!failed.events.length} testid="ghl-failed-events">
      {failed.events.map((e) => (
        <TableRow key={e.id}>
          <TableCell className="whitespace-nowrap text-slate-500">{fmtDateTime(e.received_at)}</TableCell>
          <TableCell>{e.type}</TableCell>
          <TableCell className="text-xs text-rose-600">{brand(e.error)}</TableCell>
          <TableCell><RetryBtn onClick={() => onRetry("event", e.event_id)} testid={`retry-event-${e.id}`} /></TableCell>
        </TableRow>
      ))}
    </Grid>
    <Grid title="Mensajes no enviados" head={["Fecha", "Canal", "Motivo", ""]} empty={!failed.messages.length} testid="ghl-failed-messages">
      {failed.messages.map((m) => (
        <TableRow key={m.id}>
          <TableCell className="whitespace-nowrap text-slate-500">{fmtDateTime(m.sent_at)}</TableCell>
          <TableCell>{m.channel}</TableCell>
          <TableCell className="text-xs text-rose-600">{brand(m.error_reason)}</TableCell>
          <TableCell><Link to={`/crm/conversaciones/${m.conversation_id}`} className="text-sm font-semibold text-[#1E6FD9]">Abrir</Link></TableCell>
        </TableRow>
      ))}
    </Grid>
  </div>
);
